/**
 * Split-hand touch controls plus keyboard input. Pointer ownership is explicit
 * so throttle + steering works across touch, pen and mouse without latching.
 */
import type { CameraMode } from '../car/chaseCamera';

export interface Controls {
  throttle: number;
  brake: number;
  /** -1 left .. +1 right */
  steer: number;
  /** Rising edge only; cleared after it is read. */
  consumeCameraToggle(): boolean;
  setCameraMode(mode: CameraMode): void;
  dispose(): void;
}

export type ControlButton = 'up' | 'down' | 'left' | 'right';

/** Pointer ownership independent of DOM, so multi-touch invariants are testable. */
export class PointerLatch {
  private readonly active = new Map<number, ControlButton>();

  add(pointerId: number, button: ControlButton): void {
    this.active.set(pointerId, button);
  }

  release(pointerId: number): { button: ControlButton; stillHeld: boolean } | null {
    const button = this.active.get(pointerId);
    if (!button) return null;
    this.active.delete(pointerId);
    return { button, stillHeld: this.isHeld(button) };
  }

  isHeld(button: ControlButton): boolean {
    return [...this.active.values()].includes(button);
  }

  clear(): void {
    this.active.clear();
  }
}

type Button = ControlButton;

const BUTTONS: Record<
  Button,
  { label: string; icon: string; key: string; aria: string }
> = {
  up: { label: '油門', icon: '▲', key: 'W / ↑', aria: '油門，W 或向上方向鍵' },
  down: { label: '煞車', icon: '▼', key: 'S / ↓', aria: '煞車及倒車，S 或向下方向鍵' },
  left: { label: '左轉', icon: '←', key: 'A / ←', aria: '左轉，A 或向左方向鍵' },
  right: { label: '右轉', icon: '→', key: 'D / →', aria: '右轉，D 或向右方向鍵' },
};

const KEY_MAP: Record<string, Button> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  KeyW: 'up',
  KeyS: 'down',
  KeyA: 'left',
  KeyD: 'right',
};

function makeZone(className: string, label: string): HTMLDivElement {
  const zone = document.createElement('div');
  zone.className = `control-zone ${className}`;
  zone.setAttribute('role', 'group');
  zone.setAttribute('aria-label', label);
  const caption = document.createElement('p');
  caption.className = 'control-caption';
  caption.textContent = label;
  caption.setAttribute('aria-hidden', 'true');
  zone.appendChild(caption);
  return zone;
}

export function createControls(container: HTMLElement): Controls {
  const held: Record<Button, boolean> = { up: false, down: false, left: false, right: false };
  const pointers = new PointerLatch();
  const keys = new Map<string, Button>();
  let cameraToggle = false;

  container.hidden = false;
  container.className = 'dpad';
  container.setAttribute('aria-label', '駕駛控制');

  const steerZone = makeZone('control-steer', '轉向');
  const pedalZone = makeZone('control-pedals', '速度');
  const buttons = new Map<Button, HTMLButtonElement>();

  const makeButton = (button: Button) => {
    const config = BUTTONS[button];
    const element = document.createElement('button');
    element.type = 'button';
    element.className = `dpad-btn dpad-${button}`;
    element.setAttribute('aria-label', config.aria);
    element.setAttribute('aria-pressed', 'false');
    element.setAttribute(
      'aria-keyshortcuts',
      button === 'up'
        ? 'W ArrowUp'
        : button === 'down'
          ? 'S ArrowDown'
          : button === 'left'
            ? 'A ArrowLeft'
            : 'D ArrowRight',
    );
    const icon = document.createElement('span');
    icon.className = 'control-icon';
    icon.textContent = config.icon;
    icon.setAttribute('aria-hidden', 'true');
    const label = document.createElement('span');
    label.className = 'control-label';
    label.textContent = config.label;
    const key = document.createElement('span');
    key.className = 'control-key';
    key.textContent = config.key;
    key.setAttribute('aria-hidden', 'true');
    element.append(icon, label, key);
    buttons.set(button, element);
    return element;
  };

  steerZone.append(makeButton('left'), makeButton('right'));
  pedalZone.append(makeButton('up'), makeButton('down'));
  container.appendChild(steerZone);

  const cameraButton = document.createElement('button');
  cameraButton.type = 'button';
  cameraButton.className = 'cam-btn';
  cameraButton.textContent = '視角 · C';
  cameraButton.setAttribute('aria-label', '切換駕駛席或追蹤視角');
  cameraButton.setAttribute('aria-keyshortcuts', 'C');
  cameraButton.setAttribute('aria-pressed', 'false');
  container.append(cameraButton, pedalZone);

  const keyboardHolds = (button: Button) => [...keys.values()].includes(button);
  const sync = (button: Button) => {
    const active = pointers.isHeld(button) || keyboardHolds(button);
    held[button] = active;
    const element = buttons.get(button);
    element?.classList.toggle('is-held', active);
    element?.setAttribute('aria-pressed', String(active));
  };

  const onPointerDown = (event: PointerEvent) => {
    const target = event.currentTarget as HTMLButtonElement;
    const button = [...buttons.entries()].find(([, element]) => element === target)?.[0];
    if (!button) return;
    event.preventDefault();
    target.setPointerCapture(event.pointerId);
    pointers.add(event.pointerId, button);
    sync(button);
  };

  const releasePointer = (event: PointerEvent) => {
    const released = pointers.release(event.pointerId);
    if (released) sync(released.button);
  };
  const onContextMenu = (event: Event) => event.preventDefault();

  for (const element of buttons.values()) {
    element.addEventListener('pointerdown', onPointerDown, { passive: false });
    element.addEventListener('pointerup', releasePointer);
    element.addEventListener('pointercancel', releaseAllInputs);
    element.addEventListener('lostpointercapture', releasePointer);
    element.addEventListener('contextmenu', onContextMenu);
  }

  const onCamera = (event: Event) => {
    event.preventDefault();
    cameraToggle = true;
  };
  cameraButton.addEventListener('click', onCamera);

  const onKeyDown = (event: KeyboardEvent) => {
    const button = KEY_MAP[event.code];
    if (button) {
      event.preventDefault();
      keys.set(event.code, button);
      sync(button);
    } else if (event.code === 'KeyC' && !event.repeat) {
      event.preventDefault();
      cameraToggle = true;
    }
  };
  const onKeyUp = (event: KeyboardEvent) => {
    const button = keys.get(event.code) ?? KEY_MAP[event.code];
    if (!button) return;
    event.preventDefault();
    keys.delete(event.code);
    sync(button);
  };

  function releaseAll(): void {
    pointers.clear();
    keys.clear();
    for (const button of Object.keys(held) as Button[]) sync(button);
  }

  function releaseAllInputs(_event?: Event): void {
    releaseAll();
  }

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', releaseAll);
  // A browser/OS gesture can cancel the touch sequence without delivering a
  // pointerup for every finger. Treat it as an emergency neutral input.
  container.addEventListener('touchcancel', releaseAllInputs);
  const onVisibility = () => {
    if (document.hidden) releaseAll();
  };
  document.addEventListener('visibilitychange', onVisibility);

  return {
    get throttle() {
      return held.up ? 1 : 0;
    },
    get brake() {
      return held.down ? 1 : 0;
    },
    get steer() {
      return (held.right ? 1 : 0) - (held.left ? 1 : 0);
    },
    consumeCameraToggle() {
      const value = cameraToggle;
      cameraToggle = false;
      return value;
    },
    setCameraMode(mode) {
      cameraButton.setAttribute('aria-pressed', String(mode === 'chase'));
      cameraButton.textContent = mode === 'chase' ? '追蹤 · C' : '視角 · C';
    },
    dispose() {
      releaseAll();
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', releaseAll);
      document.removeEventListener('visibilitychange', onVisibility);
      container.removeEventListener('touchcancel', releaseAllInputs);
      cameraButton.removeEventListener('click', onCamera);
      for (const element of buttons.values()) {
        element.removeEventListener('pointerdown', onPointerDown);
        element.removeEventListener('pointerup', releasePointer);
        element.removeEventListener('pointercancel', releaseAllInputs);
        element.removeEventListener('lostpointercapture', releasePointer);
        element.removeEventListener('contextmenu', onContextMenu);
      }
      container.replaceChildren();
      container.hidden = true;
    },
  };
}
